import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  readConsent,
  saveConsent,
  defaultConsent,
  hasAnalyticsConsent,
  hasAdvertisingConsent,
} from '../utils/cookieConsent';
import { applyAnalyticsConsent } from '../utils/analytics';

const CookieConsentContext = createContext(null);

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => readConsent());
  const [pendingConsent, setPendingConsent] = useState(() => !readConsent().decided);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    applyAnalyticsConsent(hasAnalyticsConsent(consent));
  }, [consent]);

  const persistConsent = useCallback((next) => {
    const saved = saveConsent(next);
    setConsent(saved);
    setPendingConsent(false);
    setSettingsOpen(false);
    return saved;
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent({ analytics: true, advertising: true });
  }, [persistConsent]);

  const rejectAll = useCallback(() => {
    persistConsent({ analytics: false, advertising: false });
  }, [persistConsent]);

  const savePreferences = useCallback((preferences) => {
    persistConsent({
      analytics: Boolean(preferences.analytics),
      advertising: Boolean(preferences.advertising),
    });
  }, [persistConsent]);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      pendingConsent,
      settingsOpen,
      setSettingsOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openSettings,
      closeSettings,
      analyticsAllowed: hasAnalyticsConsent(consent),
      advertisingAllowed: hasAdvertisingConsent(consent),
    }),
    [
      consent,
      pendingConsent,
      settingsOpen,
      acceptAll,
      rejectAll,
      savePreferences,
      openSettings,
      closeSettings,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    return {
      consent: defaultConsent(),
      pendingConsent: true,
      settingsOpen: false,
      setSettingsOpen: () => {},
      acceptAll: () => {},
      rejectAll: () => {},
      savePreferences: () => {},
      openSettings: () => {},
      closeSettings: () => {},
      analyticsAllowed: false,
      advertisingAllowed: false,
    };
  }
  return ctx;
};
